import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Loader2 } from 'lucide-react';
import { useInviteAuth } from '@/hooks/useInviteAuth';
import type { IInviteAuthFormProps, TInviteAuthMode } from '@/@types/organisation';

export function InviteAuthForm({
  email,
  token,
  orgName,
  initialMode,
  onAccepted,
}: IInviteAuthFormProps) {
  const [mode, setMode] = useState<TInviteAuthMode>(initialMode);
  const {
    password,
    setPassword,
    confirmPassword,
    setConfirmPassword,
    error,
    isPending,
    handleSubmit,
  } = useInviteAuth({ email, token, mode, onAccepted });

  const isSignUp = mode === 'signUp';

  return (
    <div className="text-center max-w-md p-8 bg-card rounded-lg border border-border">
      <h1 className="text-2xl font-bold text-foreground mb-2">Join {orgName}</h1>
      <p className="text-muted-foreground mb-6">
        {isSignUp
          ? 'Create your account to join the organisation.'
          : 'Sign in to join the organisation.'}
      </p>

      <form onSubmit={handleSubmit} className="space-y-4 text-left">
        <div>
          <Label htmlFor="invite-email" className="text-foreground-muted">
            Email
          </Label>
          <Input
            id="invite-email"
            type="email"
            value={email}
            disabled
            className="mt-1 bg-background-input border-border text-foreground opacity-60"
          />
        </div>

        <div>
          <Label htmlFor="invite-password" className="text-foreground-muted">
            Password
          </Label>
          <Input
            id="invite-password"
            type="password"
            placeholder="••••••••"
            required
            value={password}
            onChange={e => setPassword(e.target.value)}
            disabled={isPending}
            className="mt-1 bg-background-input border-border text-foreground placeholder:text-foreground-placeholder focus:border-border-hover"
          />
        </div>

        {isSignUp && (
          <div>
            <Label htmlFor="invite-confirm-password" className="text-foreground-muted">
              Confirm Password
            </Label>
            <Input
              id="invite-confirm-password"
              type="password"
              placeholder="••••••••"
              required
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              disabled={isPending}
              className="mt-1 bg-background-input border-border text-foreground placeholder:text-foreground-placeholder focus:border-border-hover"
            />
          </div>
        )}

        {error && (
          <div
            className="p-3 text-sm text-red-400 bg-red-900/20 border border-red-900/50 rounded-md"
            role="alert"
          >
            {error}
          </div>
        )}

        <Button type="submit" className="w-full" disabled={isPending}>
          {isPending ? (
            <>
              <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin mr-2" />
              {isSignUp ? 'Creating account...' : 'Signing in...'}
            </>
          ) : isSignUp ? (
            'Create Account & Join'
          ) : (
            'Sign In & Join'
          )}
        </Button>
      </form>

      <p className="mt-4 text-center text-sm text-muted-foreground">
        {isSignUp ? 'Already have an account? ' : "Don't have an account? "}
        <button
          type="button"
          className="text-link hover:underline"
          onClick={() => setMode(isSignUp ? 'signIn' : 'signUp')}
        >
          {isSignUp ? 'Sign in' : 'Create account'}
        </button>
      </p>
    </div>
  );
}
